import { documentComponentFromData } from "./DocumentComponentRegistry";

class IsibuteDocumentComponent {
  constructor({
    id,
    unstyledContent,
    styling,
    ordinal,
    documentComponentType,
    parentDocumentComponentId,
    firstChildDocumentComponentId,
    lastChildDocumentComponentId,
    previousDocumentComponentId,
    nextDocumentComponentId,
    isibuteDocument,
  }) {
    this.id = id;
    this.unstyledContent = unstyledContent;
    this.styling = styling || [];
    if (this.styling.length === 0 && unstyledContent) {
      this.styling = [{ start: 0, end: unstyledContent.length - 1, types: ['none'] }];
    }
    this.ordinal = ordinal;
    this.documentComponentType = documentComponentType;
    this.parentDocumentComponentId = parentDocumentComponentId;
    this.firstChildDocumentComponentId = firstChildDocumentComponentId;
    this.lastChildDocumentComponentId = lastChildDocumentComponentId;
    this.previousDocumentComponentId = previousDocumentComponentId;
    this.nextDocumentComponentId = nextDocumentComponentId;
    this.isibuteDocument = isibuteDocument;
  }

  asJSON = () => {
    return {
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: this.documentComponentType,
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
    };
  };

  deepCopy = () => {
    return new this.constructor({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: this.documentComponentType,
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  titleizedCopy = () => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: 'DocumentTitle',
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  subtitleizedCopy = () => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: 'DocumentSubtitle',
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  paragraphizedCopy = () => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: 'DocumentParagraph',
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  sectionTitleizedCopy = ({ size }) => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: 'DocumentSectionTitle',
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
      size,
    });
  };

  emphasizedCopy = () => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: 'DocumentEmphasis',
      parentDocumentComponentId: this.parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  citationizedCopy = ({ parentDocumentComponentId }) => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal, // this needs to be updated
      documentComponentType: 'DocumentCitation',
      parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  unorderedListItemizedCopy = ({ parentDocumentComponentId }) => {
    return documentComponentFromData({
      id: this.id,
      unstyledContent: this.unstyledContent,
      styling: this.styling.map(style => {return {...style}}),
      ordinal: this.ordinal,
      documentComponentType: 'DocumentUnorderedListItem',
      parentDocumentComponentId,
      firstChildDocumentComponentId: this.firstChildDocumentComponentId,
      lastChildDocumentComponentId: this.lastChildDocumentComponentId,
      previousDocumentComponentId: this.previousDocumentComponentId,
      nextDocumentComponentId: this.nextDocumentComponentId,
      isibuteDocument: this.isibuteDocument,
    });
  };

  keyPath = () => {
    return [this.id];
  };

  getComponentForArrowLeft = () => {
    const previousDocumentComponent = this.getPreviousDocumentComponent();

    if (previousDocumentComponent?.lastChildDocumentComponentId) {
      return this.isibuteDocument.getDocumentComponentByKeyPath({
        keyPath: [
          previousDocumentComponent.id,
          previousDocumentComponent.lastChildDocumentComponentId,
        ],
      });
    }

    return previousDocumentComponent;
  };

  getPreviousDocumentComponent = () => {
    return this.isibuteDocument.getDocumentComponentByKeyPath({
      keyPath: [this.previousDocumentComponentId],
    });
  };

  getComponentForArrowRight = () => {
    const nextDocumentComponent = this.getNextDocumentComponent();

    if (nextDocumentComponent?.firstChildDocumentComponentId) {
      return this.isibuteDocument.getDocumentComponentByKeyPath({
        keyPath: [
          nextDocumentComponent.id,
          nextDocumentComponent.firstChildDocumentComponentId,
        ],
      });
    }

    return nextDocumentComponent;
  };

  getNextDocumentComponent = () => {
    return this.isibuteDocument.getDocumentComponentByKeyPath({
      keyPath: [this.nextDocumentComponentId],
    });
  };

  getHrefAtPosition = (position) => {
    for (const style of this.styling) {
      if (position >= style.start && position <= style.end && style.types.includes('a') && style.href) {
        return style.href;
      }
    }
    return null;
  };

  getStyleTypesAtPosition = (position) => {
    const collected = [];
    for (const style of this.styling) {
      if (position >= style.start && position <= style.end) {
        for (const type of style.types) {
          if (!collected.includes(type)) {
            collected.push(type);
          }
        }
      }
    }
    return collected.length > 0 ? collected : ['none'];
  };

  // Returns only the style types that are present in every styling segment
  // overlapping the range. This intersection behavior means a style like 'b'
  // is only reported as active if the entire range is bold — not just part of it.
  getStyleTypesInRange = (start, end) => {
    let result = null;

    for (const style of this.styling) {
      if (style.end < start || style.start > end) continue;

      if (result === null) {
        // Seed with the first overlapping segment's types
        result = [...style.types];
      } else {
        // Narrow to only types shared with this segment
        result = result.filter(t => style.types.includes(t));
      }
    }

    // null means no segments overlapped the range at all
    return result || ['none'];
  };

  applyStyle = (instruction) => {
    let iteratorIndex = 0;

    while (!!this.styling[iteratorIndex]) {
      let style = this.styling[iteratorIndex];

      if (instruction.end < style.start || instruction.start > style.end) {
        iteratorIndex += 1;
        continue;
      }

      style = this.styling.splice(iteratorIndex, 1)[0];

      const newStyleWithApplicableRange = {
        start: instruction.start,
        end: instruction.end,
        types: instruction.types,
        href: instruction.href,
      };

      if (newStyleWithApplicableRange.start < style.start) {
        newStyleWithApplicableRange.start = style.start;
      }

      if (newStyleWithApplicableRange.end > style.end) {
        newStyleWithApplicableRange.end = style.end;
      }

      if (style.start < newStyleWithApplicableRange.start) {
        const preSegment = {
          start: style.start,
          end: newStyleWithApplicableRange.start - 1,
          types: [...style.types],
        };
        if (style.href !== undefined) preSegment.href = style.href;
        this.styling.splice(iteratorIndex, 0, preSegment);

        iteratorIndex += 1;
      }

      const mergedTypes = this.mergeStyleTypes([...style.types], newStyleWithApplicableRange.types);
      const mergedHref = mergedTypes.includes('a')
        ? (newStyleWithApplicableRange.href !== undefined ? newStyleWithApplicableRange.href : style.href)
        : undefined;
      const newStyleMergedWithPreexistingStyle = {
        start: newStyleWithApplicableRange.start,
        end: newStyleWithApplicableRange.end,
        types: mergedTypes,
      };
      if (mergedHref !== undefined) newStyleMergedWithPreexistingStyle.href = mergedHref;

      this.styling.splice(
        iteratorIndex,
        0,
        newStyleMergedWithPreexistingStyle,
      );

      iteratorIndex += 1;

      if (style.end > newStyleWithApplicableRange.end) {
        const postSegment = {
          start: newStyleWithApplicableRange.end + 1,
          end: style.end,
          types: [...style.types],
        };
        if (style.href !== undefined) postSegment.href = style.href;
        this.styling.splice(iteratorIndex, 0, postSegment);

        iteratorIndex += 1;
      }
    }
  };

  // Removes specific style types from a character range, mutating this.styling
  // in place. Counterpart to applyStyle — used when toggling off a style that's
  // active across the entire selection.
  //
  // Each overlapping segment is spliced out and replaced with up to three parts:
  //   1. Pre-segment:  portion before the instruction range, kept unchanged
  //   2. Overlap:      portion within the instruction range, with the specified
  //                    types subtracted (falls back to 'none' if nothing remains)
  //   3. Post-segment: portion after the instruction range, kept unchanged
  //
  // href is preserved on pre/post segments unconditionally, and on the overlap
  // segment only if the link type 'a' survives the removal.
  removeStyle = (instruction) => {
    // Manual index because we splice during iteration
    let iteratorIndex = 0;

    while (!!this.styling[iteratorIndex]) {
      let style = this.styling[iteratorIndex];

      if (instruction.end < style.start || instruction.start > style.end) {
        iteratorIndex += 1;
        continue;
      }

      style = this.styling.splice(iteratorIndex, 1)[0];

      const applicableStart = Math.max(instruction.start, style.start);
      const applicableEnd = Math.min(instruction.end, style.end);

      // Pre-segment: preserve the portion left of the overlap unchanged
      if (style.start < applicableStart) {
        const preSegment = {
          start: style.start,
          end: applicableStart - 1,
          types: style.types,
        };
        if (style.href !== undefined) preSegment.href = style.href;
        this.styling.splice(iteratorIndex, 0, preSegment);
        iteratorIndex += 1;
      }

      // Overlap segment: subtract the instruction's types from this segment
      const remainingTypes = style.types.filter(t => !instruction.types.includes(t));
      const overlapSegment = {
        start: applicableStart,
        end: applicableEnd,
        types: remainingTypes.length > 0 ? remainingTypes : ['none'],
      };
      if (remainingTypes.includes('a') && style.href !== undefined) {
        overlapSegment.href = style.href;
      }
      this.styling.splice(iteratorIndex, 0, overlapSegment);
      iteratorIndex += 1;

      // Post-segment: preserve the portion right of the overlap unchanged
      if (style.end > applicableEnd) {
        const postSegment = {
          start: applicableEnd + 1,
          end: style.end,
          types: style.types,
        };
        if (style.href !== undefined) postSegment.href = style.href;
        this.styling.splice(iteratorIndex, 0, postSegment);
        iteratorIndex += 1;
      }
    }
  };

  mergeStyleTypes = (preexistingTypes, newTypes) => {
    if ((preexistingTypes.length == 1) && (preexistingTypes[0] == 'none')) {
      return newTypes;
    }

    const index = preexistingTypes.indexOf(newTypes[0]);

    if (index > -1) {
      preexistingTypes.splice(index, 1);
      return preexistingTypes.length > 0 ? preexistingTypes : ['none'];
    }

    return preexistingTypes.concat(newTypes);
  };

  splitStyling = ({ splitIndex }) => {
    const firstHalfOfStyling = [];
    const secondHalfOfStyling = [];

    this.styling.forEach((style) => {
      if (style.start < splitIndex && style.end < splitIndex) {
        firstHalfOfStyling.push(style);
      }

      if (style.start < splitIndex && splitIndex <= style.end) {
        const firstHalf = { start: style.start, end: splitIndex - 1, types: style.types };
        if (style.href !== undefined) firstHalf.href = style.href;
        firstHalfOfStyling.push(firstHalf);
        const secondHalf = { start: 0, end: style.end - splitIndex + 1, types: style.types };
        if (style.href !== undefined) secondHalf.href = style.href;
        secondHalfOfStyling.push(secondHalf);
      }

      if (splitIndex == style.start && splitIndex <= style.end) {
        const segment = { start: 0, end: style.end - splitIndex + 1, types: style.types };
        if (style.href !== undefined) segment.href = style.href;
        secondHalfOfStyling.push(segment);
      }

      if (splitIndex < style.start && splitIndex < style.end) {
        const segment = { start: style.start - splitIndex + 1, end: style.end - splitIndex + 1, types: style.types };
        if (style.href !== undefined) segment.href = style.href;
        secondHalfOfStyling.push(segment);
      }
    });

    if (secondHalfOfStyling.length == 0) {
      secondHalfOfStyling.push({
        start: 0,
        end: 0,
        types: ['none'],
      });
    }

    return {
      firstHalfOfStyling,
      secondHalfOfStyling,
    };
  };

  joinStyling = ({ firstHalfOfStyling, secondHalfOfStyling }) => {
    const joinedStyling = [];

    firstHalfOfStyling.forEach((style) => {
      joinedStyling.push(style);
    });

    const endOfFirstHalfOfStyling = firstHalfOfStyling.sort((thisStyle, thatStyle) => {
      return thisStyle.end - thatStyle.end;
    }).pop().end;

    secondHalfOfStyling.forEach((style) => {
      const segment = style.start == 0
        ? { start: style.start + endOfFirstHalfOfStyling + 1, end: style.end + endOfFirstHalfOfStyling, types: style.types }
        : { start: style.start + endOfFirstHalfOfStyling, end: style.end + endOfFirstHalfOfStyling, types: style.types };
      if (style.href !== undefined) segment.href = style.href;
      joinedStyling.push(segment);
    });

    return joinedStyling;
  };

  joinUnstyledContent = ({ firstHalfOfUnstyledConent, secondHalfOfUnstyledContent }) => {
    return firstHalfOfUnstyledConent + secondHalfOfUnstyledContent.substring(1);
  };
};

export default IsibuteDocumentComponent;